import { Controller } from "@hotwired/stimulus"
// CountUp imported directly here instead of read from window.CountUp.
// The bridge had a Turbo-Drive load-order race — application.js
// auto-registers this controller, and on a cache-restore navigation
// connect() could fire before the live pack evaluated, leaving
// CountUp undefined and silently downgrading to plain text rendering.
// Direct import is stable; ~4KB duplication is acceptable.
import { CountUp } from "countup.js"

/**
 * KPI-region owner. Manages CountUp instances for the 5 numeric cards
 * plus the Goal progress bar. Respects prefers-reduced-motion (snaps
 * instantly instead of animating) per PRD §7.3.3.
 */
const REDUCE_MOTION = () => window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches

export default class extends Controller {
  static targets = ["totalLeads", "leadsThisHour", "todayTotal",
                    "activeReps", "scansPerHour",
                    "goalCount", "goalProgressBar", "goalProgressPct",
                    // LRPO-5130 (re-open) — the last two cards that didn't refresh
                    // on the poll: the Target value + the This-Hour comparison delta.
                    "targetValue", "thisHourDelta"]

  connect() {
    this._countUps = {}
    // LRPO-5130 (rework) — cached by applyThisHourDelta on every real snapshot;
    // lets bumpThisHourDeltaOptimistic recompute the delta arrow right after a
    // scan bump instead of waiting for the next poll (see that method). Stays
    // undefined until the first snapshot lands (cable-connect fires one
    // immediately), which the optimistic path treats as "nothing to do yet."
    this._lastPreviousHour = undefined
  }

  // Task 24 a11y follow-up: keyboard nav for the mobile horizontal-scroll KPI strip.
  scrollKeys(event) {
    const container = event.currentTarget
    const cardWidth = container.querySelector('.live-kpi-strip__card-col')?.offsetWidth || 0
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault()
        container.scrollLeft += cardWidth
        break
      case 'ArrowLeft':
        event.preventDefault()
        container.scrollLeft -= cardWidth
        break
      case 'Home':
        event.preventDefault()
        container.scrollLeft = 0
        break
      case 'End':
        event.preventDefault()
        container.scrollLeft = container.scrollWidth
        break
    }
  }

  // Returns the resolved "next" value (or undefined when the target is
  // absent) — LRPO-5130 (rework) threads this through to
  // bumpThisHourDeltaOptimistic so it can recompute the delta from the exact
  // value just applied, rather than re-reading textContent while CountUp's
  // rAF-driven animation may not have painted it yet.
  animateDelta(name, delta) {
    const cap = `${name[0].toUpperCase()}${name.slice(1)}`
    if (!this[`has${cap}Target`]) return
    const current = parseInt(this[`${name}Target`].textContent.replace(/,/g, ""), 10) || 0
    const next = current + delta
    this.setValue(name, next)
    return next
  }

  setValue(name, value) {
    const cap = `${name[0].toUpperCase()}${name.slice(1)}`
    if (!this[`has${cap}Target`]) return

    const target  = this[`${name}Target`]
    const current = parseInt(target.textContent.replace(/,/g, ""), 10) || 0
    const next    = Number.isFinite(+value) ? +value : current

    const reduce = REDUCE_MOTION()

    // LRPO-5557 — a Turbo Stream that re-renders the Goal Strip (an inline target
    // edit replaces the #live-goal-edit frame) swaps the goalCount/targetValue
    // span for a fresh DOM node. Stimulus re-registers the *target* getter to the
    // new node, but the cached CountUp instance still holds a hard reference to
    // the now-detached OLD element (countup.js keeps it as `.el`). Reusing that
    // stale instance means the next live scan's .update() writes into the
    // invisible orphan, so the visible counter next to the target freezes until a
    // full page refresh. Discard a CountUp whose bound element is no longer the
    // current target (or has been detached from the document) and rebuild it
    // below against the live node, re-binding the counter to what the user sees.
    const cached = this._countUps[name]
    if (cached && (cached.el !== target || !target.isConnected)) {
      this._countUps[name] = null
    }

    if (!this._countUps[name]) {
      this._countUps[name] = new CountUp(target, next, {
        startVal:  current,
        duration:  reduce ? 0 : 0.6,
        useEasing: !reduce,
        separator: ","
      })
      this._countUps[name].start()
    } else {
      this._countUps[name].update(next)
    }
  }

  applyServerValues(data) {
    this.setValue("totalLeads",    data.total_leads)
    this.setValue("todayTotal",    data.today_total)
    this.setValue("leadsThisHour", data.leads_this_hour)
    this.setValue("activeReps",    data.active_reps_count)
    // LRPO-5130 / LRPO-5285 — drive the Scans/Hr card from the snapshot's
    // minute-prorated projection so it refreshes live alongside the other KPIs
    // instead of staying frozen at its page-load value. setValue no-ops on a
    // missing key.
    this.setValue("scansPerHour",  data.scans_per_hour)
    // LRPO-5135 — the goal "current leads" count is total_leads (see _goal_strip),
    // but only the bar/% refreshed on poll, leaving the count frozen at its
    // page-load value until a full refresh. Update it here so it tracks the
    // totalLeads card. setValue no-ops when there's no target (goalCount absent).
    this.setValue("goalCount",     data.total_leads)
    // LRPO-5130 (re-open) — refresh the Target value + the This-Hour comparison
    // delta, the two cards that previously only rendered server-side and stayed
    // frozen until a full page refresh. setValue no-ops when the target is absent.
    this.setValue("targetValue",   data.lead_target)
    this.applyThisHourDelta(data)
    this.updateGoalProgress(data.goal_progress_pct)
  }

  // LRPO-5130 (re-open) — recompute the "This Hour vs last hour" comparison delta
  // (color / arrow / number) on each snapshot. The raw This-Hour count refreshes
  // via setValue("leadsThisHour"); this refreshes the delta indicator next to it,
  // which used to render once server-side and never update. Hidden until a prior
  // hour exists (matching the server-rendered `d-none` gate).
  applyThisHourDelta(data) {
    if (!this.hasThisHourDeltaTarget) return

    // LRPO-5130 (rework) — cache the previous-hour figure. It's fixed for the
    // remainder of the clock hour, so bumpThisHourDeltaOptimistic can reuse it
    // to recompute this delta immediately after a scan bump instead of
    // waiting for the next poll.
    this._lastPreviousHour = Number(data.leads_previous_hour)

    const el   = this.thisHourDeltaTarget
    const prev = this._lastPreviousHour
    const now  = Number(data.leads_this_hour)
    if (!Number.isFinite(prev) || prev <= 0 || !Number.isFinite(now)) {
      el.classList.add("d-none")
      return
    }

    const delta = now - prev
    const cls   = delta < 0 ? "text-danger" : (delta > 0 ? "text-success" : "text-muted")
    const icon  = delta < 0 ? "bi-arrow-down-right" : (delta > 0 ? "bi-arrow-up-right" : "bi-dash")

    // The base <small> carries only the color class (+ d-none when hidden), so a
    // full className reset both re-shows it and swaps the color in one write.
    el.className = cls
    const iconEl  = el.querySelector("[data-this-hour-delta-icon]")
    const valueEl = el.querySelector("[data-this-hour-delta-value]")
    if (iconEl)  iconEl.className = `bi ${icon}`
    if (valueEl) valueEl.textContent = Math.abs(delta)
  }

  // LRPO-5130 (rework) — QA flagged Metrics and the Live Scan Feed updating
  // out of sync: on a new-lead scan the feed row + the Total/Today/This-Hour
  // counts already land in the same tick as the broadcast (see
  // live_dashboard_controller#_onScanReceived), but the This-Hour delta arrow
  // next to the count sat stale for the ~1s debounced reconciliation poll —
  // a visible "fresh feed row, stale comparison arrow" gap. leads_previous_hour
  // doesn't change within the clock hour, so the cached value from the last
  // real snapshot (see applyThisHourDelta) is still correct; only
  // leads_this_hour needs the freshly-bumped figure, passed in by the caller
  // (nextThisHour) since re-reading textContent here could catch CountUp's
  // animation mid-flight. A no-op before the first snapshot has cached a
  // previous-hour value — the debounced poll covers that narrow window.
  bumpThisHourDeltaOptimistic(nextThisHour) {
    if (!this.hasThisHourDeltaTarget) return
    if (!Number.isFinite(this._lastPreviousHour) || this._lastPreviousHour <= 0) return

    this.applyThisHourDelta({
      leads_previous_hour: this._lastPreviousHour,
      leads_this_hour:     nextThisHour
    })
  }

  updateGoalProgress(serverPct) {
    if (!this.hasGoalProgressBarTarget) return

    let pct = serverPct
    if (pct == null && this.hasGoalCountTarget) {
      const count = parseInt(this.goalCountTarget.textContent.replace(/,/g, ""), 10) || 0
      const bar   = this.goalProgressBarTarget.closest("[role='progressbar']")
      const max   = parseInt(bar?.getAttribute("aria-valuemax"), 10) || 100
      pct = max > 0 ? Math.round((count / max) * 100) : 0
    }
    // The VISUAL progress (bar width + aria-valuenow) caps at 100% so it
    // never overflows once the goal is exceeded. The TEXTUAL label shows the
    // figure with a percent sign up to 100% then collapses to "Over goal"
    // once strictly above 100 — a raw over-100 figure read as an unrealistic
    // "120%"/"10200%" when a tiny target was smashed (LRPO-5238). This label
    // must stay byte-identical to the server-rendered HTML / snapshot / cable
    // payloads (goal_progress_label) so it never flickers on the first tick
    // (LRPO-5141).
    const truePct = Math.round(Number.isFinite(+pct) ? +pct : 0)
    const clamped = Math.max(0, Math.min(100, truePct))
    this.goalProgressBarTarget.style.width = `${clamped}%`
    this.goalProgressBarTarget.closest("[role='progressbar']")?.setAttribute("aria-valuenow", clamped)
    if (this.hasGoalProgressPctTarget) this.goalProgressPctTarget.textContent = truePct > 100 ? "Over goal" : `${truePct}%`
  }
}
